<template>
  <div
    class="diary-image-upload"
    :aria-busy="busy"
    @mousedown.stop
    @dragover.prevent
    @drop.prevent.stop="insertFiles($event.dataTransfer.files)"
  >
    <button
      type="button"
      :disabled="busy"
      @click="openGallery"
    >
      From Gallery
    </button>
    <label>
      Choose images or drop them here
      <input
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        :disabled="busy"
        @change="onFileChange"
      >
    </label>
    <p
      v-if="busy"
      role="status"
    >
      Preparing images…
    </p>
    <p
      v-if="error"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
import { mdiImageOutline } from '@mdi/js'
import readEditorImage from '@/utils/editor-image'

export default {
  title: 'diaryImage',
  description: 'Insert Image',
  icon: `<svg viewBox="0 0 24 24"><path d="${mdiImageOutline}" /></svg>`,
  props: {
    options: { type: Object, required: true },
  },
  data() {
    return { busy: false, error: '', cancelled: false }
  },
  beforeDestroy() {
    this.cancelled = true
    if (this.busy) this.options.onImageProcessing(false)
  },
  methods: {
    openGallery() {
      this.$parent.closeDashboard()
      this.options.onOpenGallery()
    },
    async onFileChange(event) {
      const input = event.target
      await this.insertFiles(input.files)
      input.value = ''
    },
    async insertFiles(files) {
      if (this.busy || !files.length) return
      this.busy = true
      this.error = ''
      this.options.onImageProcessing(true)
      const images = []
      const failed = []
      try {
        // Insert the batch together to preserve its order and the editor's saved caret.
        await Array.from(files).reduce(async (previous, file) => {
          // Decode one at a time to avoid holding several full-size photos in memory.
          await previous
          if (this.cancelled) return
          try {
            const source = await readEditorImage(file)
            const image = document.createElement('img')
            image.src = source
            image.alt = file.name || 'Diary image'
            image.style.maxWidth = '100%'
            images.push(image.outerHTML)
          } catch (error) {
            failed.push(file.name || 'Image')
          }
        }, Promise.resolve())
        if (this.cancelled) return
        if (images.length) {
          this.$emit('exec', 'insertHTML', images.join(''))
          await this.$nextTick()
        }
        if (failed.length) {
          this.error = `Could not insert ${failed.join(', ')}. Please try another image or a JPG/PNG copy.`
        } else {
          this.$parent.closeDashboard()
        }
      } finally {
        this.busy = false
        if (!this.cancelled) this.options.onImageProcessing(false)
      }
    },
  },
}
</script>

<style scoped>
.diary-image-upload {
  padding: 16px;
  width: 280px;
  max-width: 75vw;
}
.diary-image-upload input {
  display: block;
  max-width: 100%;
  margin-top: 12px;
}
.diary-image-upload label {
  display: block;
  margin-top: 16px;
}
.diary-image-upload p {
  margin: 12px 0 0;
}
.diary-image-upload [role="alert"] {
  color: #b00020;
}
</style>
